// Server-authoritative Kiosk (video/chamber) screen config, with a localStorage
// write-through cache for offline resilience on unattended 24/7 screens.
// Source of truth: GET/POST /api/secure/kiosk/my-screen-config.
//
// The write-through cache reuses the existing `kiosk-config` object (keys
// SCREEN_DEVICE_CONFIG_ID / CURRENT_VIRTUAL_ROOM_ID) that KioskLayout already
// reads, so the chamber screen consumes server-sourced config with no change.
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'

const CACHE_KEY = 'kiosk-config'

function readKioskTarget(raw) {
  try {
    const cfg = raw ? JSON.parse(raw) : null
    if (cfg?.SCREEN_DEVICE_CONFIG_ID && cfg?.CURRENT_VIRTUAL_ROOM_ID) {
      return {
        screenDeviceConfigId: cfg.SCREEN_DEVICE_CONFIG_ID,
        virtualChamberRoomId: cfg.CURRENT_VIRTUAL_ROOM_ID,
      }
    }
    return null
  } catch {
    return null
  }
}

export function readCachedKioskConfig() {
  return readKioskTarget(
    localStorage.getItem(CACHE_KEY) || localStorage.getItem('ROLE_CA_CONFIG'),
  )
}

export function cacheKioskConfig(cfg) {
  try {
    if (cfg && cfg.screenDeviceConfigId && cfg.virtualChamberRoomId) {
      // Merge into the existing kiosk-config object so KioskLayout (which reads
      // SCREEN_DEVICE_CONFIG_ID / CURRENT_VIRTUAL_ROOM_ID from it) picks it up,
      // without clobbering any other locally-stored keys.
      const raw = localStorage.getItem(CACHE_KEY)
      const existing = raw ? JSON.parse(raw) : {}
      const merged = {
        ...existing,
        SCREEN_DEVICE_CONFIG_ID: cfg.screenDeviceConfigId,
        CURRENT_VIRTUAL_ROOM_ID: cfg.virtualChamberRoomId,
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(merged))
      // Keep the legacy ROLE_CA_CONFIG mirror in sync during the transition.
      localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(merged))
    }
  } catch {
    /* private-browsing / quota — cache is best-effort */
  }
}

function buildKioskConfigEndpoint(screenDeviceConfigId) {
  const normalizedScreenDeviceConfigId =
    typeof screenDeviceConfigId === 'string' ? screenDeviceConfigId.trim() : ''
  if (!normalizedScreenDeviceConfigId) return '/api/secure/kiosk/my-screen-config'
  return `/api/secure/kiosk/my-screen-config?screenDeviceConfigId=${encodeURIComponent(
    normalizedScreenDeviceConfigId,
  )}`
}

// Returns { screenDeviceConfigId, virtualChamberRoomId } from the server for a
// specific physical screen config when supplied, or null. Throws on network
// error so callers can decide to fall back to the cache.
export async function fetchKioskConfigFromServer(screenDeviceConfigId = null) {
  const res = await getCPDataWithoutParams(buildKioskConfigEndpoint(screenDeviceConfigId))
  if (res?.status !== 200) {
    // A handled non-200 (e.g. 4xx) means "no config", not a transport failure.
    if (res?.status) return null
    throw new Error('network')
  }
  const assignment = res?.data?.data?.assignment
  if (assignment?.screenDeviceConfigId && assignment?.virtualChamberRoomId) {
    return {
      screenDeviceConfigId: assignment.screenDeviceConfigId,
      virtualChamberRoomId: assignment.virtualChamberRoomId,
    }
  }
  return null
}

export async function saveKioskConfigToServer({ screenDeviceConfigId, virtualChamberRoomId }) {
  const res = await postCPDataWithoutParams('/api/secure/kiosk/my-screen-config', {
    screenDeviceConfigId,
    virtualChamberRoomId,
  })
  if (res?.status === 200) {
    cacheKioskConfig({ screenDeviceConfigId, virtualChamberRoomId })
  }
  return res
}

// Legacy localStorage kiosk-config used before server-authoritative config.
// Read once to migrate existing chamber screens to the server. (Same store the
// cache reuses, so this also returns the offline cache.)
export function readLegacyKioskConfigFromLocal() {
  return readKioskTarget(
    localStorage.getItem('kiosk-config') || localStorage.getItem('ROLE_CA_CONFIG'),
  )
}
